import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { map, Observable } from 'rxjs'
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request'
import { Doctor } from '../store/interfaces/doctor'
import { ApiService } from './api.service'
import { environment } from '../../environments/environment'
import { unwrapCrudFirstItem, unwrapCrudList, type CrudListResponse } from '../common/utils/crud-response.util'
import { DataScope } from '../permissions/permission.types'
import { RegistrationStatusFilter } from '../common/utils/registration-status.util'
@Injectable({ providedIn: 'root' })
export class DoctorService {
  private apiUrl = `doctors`
  private readonly base = `${environment.apiUrl}/`

  constructor(
    private apiService: ApiService,
    private http: HttpClient,
  ) {}

  getListPaginated(
    filters: {
      firstName?: string
      lastName?: string
      email?: string
      channelIds?: string[]
      specialtyIds?: string[]
      roleIds?: string[]
      page?: number
      limit?: number
      dataScope?: DataScope
      contractIds?: string[]
      activeOnly?: boolean
      registrationStatus?: RegistrationStatusFilter
    } = {},
  ): Observable<{ data: Doctor[]; count: number; total: number; page: number; pageCount: number }> {
    let params = new HttpParams()
    if (filters.firstName) params = params.append('filter', `firstName||$contL||${filters.firstName}`)
    if (filters.lastName) params = params.append('filter', `lastName||$contL||${filters.lastName}`)
    if (filters.email) params = params.append('filter', `email||$contL||${filters.email}`)

    const registrationStatus = filters.registrationStatus ?? ''
    if (registrationStatus === 'inactive') {
      params = params.append('filter', 'isActiveMedical||$eq||false')
    } else {
      if (filters.activeOnly !== false) {
        params = params.append('filter', 'isActiveMedical||$eq||true')
        params = params.append('filter', 'canLogin||$eq||true')
      }
      if (registrationStatus === 'pending') {
        params = params.append('filter', 'mustSetPasswordNextLogin||$eq||true')
      } else if (registrationStatus === 'active') {
        params = params.append('filter', 'mustSetPasswordNextLogin||$eq||false')
      }
    }
    if (filters.channelIds?.length) params = params.set('channelIds', filters.channelIds.join(','))
    if (filters.specialtyIds?.length) params = params.set('specialtyIds', filters.specialtyIds.join(','))
    if (filters.roleIds?.length) params = params.set('roleIds', filters.roleIds.join(','))
    if (filters.dataScope) params = params.set('dataScope', filters.dataScope)
    params = params.append('join', 'channels')
    params = params.append('join', 'channels.channel')
    params = params.append('join', 'specialties')
    params = params.append('join', 'specialties.specialty')
    params = params.append('join', 'doctorRoles')
    params = params.append('join', 'userBase')
    params = params.set('sort', 'firstName,ASC')
    params = params.set('limit', String(filters.limit ?? 20))
    params = params.set('page', String(filters.page ?? 1))
    return this.http.get<any>(`${this.base}${this.apiUrl}`, { params }).pipe(
      map((res) => {
        if (Array.isArray(res)) {
          return {
            data: res as Doctor[],
            count: res.length,
            total: res.length,
            page: Number(filters.page ?? 1),
            pageCount: 1,
          }
        }

        const data = Array.isArray(res?.data) ? (res.data as Doctor[]) : []
        return {
          data,
          count: Number(res?.count ?? data.length),
          total: Number(res?.total ?? data.length),
          page: Number(res?.page ?? filters.page ?? 1),
          pageCount: Number(res?.pageCount ?? 1),
        }
      }),
    )
  }

  /** Lightweight list for dropdowns — dedicated endpoint, no relation joins. */
  getDoctorsForSelect(_dataScope: DataScope = 'global'): Observable<Doctor[]> {
    const params = new HttpParams().set('limit', '500')
    return this.http
      .get<CrudListResponse<Doctor>>(`${this.base}${this.apiUrl}/select-options`, { params })
      .pipe(map((res) => unwrapCrudList(res)))
  }

  getDoctorByUserBaseId(userBaseId: string | number): Observable<Doctor | null> {
    const query = RequestQueryBuilder.create({
      search: { userBaseId: { [CondOperator.EQUALS]: userBaseId } },
      join: [{ field: 'channels' }, { field: 'specialties' }, { field: 'contractDoctors' }, { field: 'doctorRoles' }],
    })
    return this.apiService
      .get<CrudListResponse<Doctor>>(this.apiUrl, query)
      .pipe(map((res) => unwrapCrudFirstItem(res)))
  }

  getActiveDoctors(): Observable<Doctor[]> {
    const query = RequestQueryBuilder.create({
      search: { isActiveMedical: { [CondOperator.EQUALS]: true } },
      sort: { field: 'firstName', order: 'ASC' },
      join: [{ field: 'channels' }, { field: 'specialties' }, { field: 'contractDoctors' }, { field: 'doctorRoles' }],
    }).setLimit(500)
    return this.apiService.get<CrudListResponse<Doctor>>(this.apiUrl, query).pipe(map((res) => unwrapCrudList(res)))
  }

  getDoctorsByIds(ids: number[]): Observable<Doctor[]> {
    const query = RequestQueryBuilder.create({
      search: { id: { [CondOperator.IN]: ids } },
      sort: { field: 'firstName', order: 'ASC' },
      join: [{ field: 'channels' }, { field: 'specialties' }, { field: 'contractDoctors' }, { field: 'doctorRoles' }],
    })
    return this.apiService.get<CrudListResponse<Doctor>>(this.apiUrl, query).pipe(map((res) => unwrapCrudList(res)))
  }

  getDoctorById(id: string | number): Observable<Doctor | null> {
    const params = new HttpParams().set('join', 'userBase')
    return this.http
      .get<CrudListResponse<Doctor>>(`${this.base}${this.apiUrl}/${id}`, { params })
      .pipe(map((res) => unwrapCrudFirstItem(res)))
  }

  updateDoctor(id: string | number, body: Partial<Doctor>): Observable<Doctor> {
    return this.http.patch<Doctor>(`${this.base}${this.apiUrl}/${id}`, body)
  }
}
